import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatApiError, fileSrc } from "@/lib/api";
import AvailabilityCalendar from "@/components/AvailabilityCalendar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ShieldCheck, Upload, X, Loader2, CheckCircle2, Phone, Mail, Star, Clock } from "lucide-react";
import { computePricing, lineFromService, suggestTravel, eur } from "@/lib/pricing";
import { toast } from "sonner";

const EMPTY = {
  first_name: "", last_name: "", phone: "", email: "", address: "", company: "",
  make: "", model: "", year: "",
  plate: "", service: "", condition: "", comment: "", photos: [],
};

export default function PublicRequest() {
  const [info, setInfo] = useState(null);
  const [showcase, setShowcase] = useState({ media: [], reviews: [], faqs: [] });
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [busyPhoto, setBusyPhoto] = useState(false);
  const [dates, setDates] = useState([]);
  const [slotData, setSlotData] = useState(null);
  const [slotLoading, setSlotLoading] = useState(false);
  const [slotTime, setSlotTime] = useState("");
  const inputRef = useRef(null);
  const { slug } = useParams();
  const [notFound, setNotFound] = useState(false);
  const [sim, setSim] = useState({});

  useEffect(() => {
    if (!slug) { setNotFound(true); return; }
    api.get(`/public/business-info?slug=${encodeURIComponent(slug)}`).then((r) => setInfo(r.data)).catch(() => setNotFound(true));
    api.get(`/public/showcase?slug=${encodeURIComponent(slug)}`).then((r) => setShowcase(r.data || { media: [], reviews: [], faqs: [] })).catch(() => {});
  }, [slug]);

  // Load time slots for the primary selected date
  useEffect(() => {
    if (!slug || dates.length === 0) { setSlotData(null); setSlotTime(""); return; }
    setSlotLoading(true); setSlotTime("");
    api.get(`/public/slots`, { params: { slug, date: dates[0] } })
      .then((r) => setSlotData(r.data))
      .catch(() => setSlotData(null))
      .finally(() => setSlotLoading(false));
  }, [slug, dates]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const bf = info?.booking_fields || { vehicle: true, email: true, address: false, company: false, comment: true, service: true };

  const toggleDate = (key) => {
    setDates((prev) => {
      if (prev.includes(key)) return prev.filter((d) => d !== key);
      if (prev.length >= 2) return [prev[1], key];
      return [...prev, key];
    });
  };

  const uploadPhotos = async (files) => {
    setBusyPhoto(true);
    const uploaded = [];
    for (const file of Array.from(files)) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post(`/public/upload?slug=${encodeURIComponent(slug)}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push({ url: data.url, path: data.path, filename: data.filename });
      } catch { toast.error("Échec de l'envoi d'une photo"); }
    }
    setForm((f) => ({ ...f, photos: [...f.photos, ...uploaded] }));
    setBusyPhoto(false);
  };

  const availableSlots = (slotData?.slots || []).filter((x) => x.available);
  const hasSlots = (slotData?.slots || []).length > 0;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.phone) {
      return toast.error("Merci de renseigner prénom, nom et téléphone");
    }
    if (bf.vehicle && !form.make) {
      return toast.error("Merci de renseigner la marque du véhicule");
    }
    if (dates.length === 0) {
      return toast.error("Merci de sélectionner au moins une date disponible dans le calendrier");
    }
    if (hasSlots && !slotTime) {
      return toast.error("Merci de choisir un créneau horaire disponible");
    }
    const openHour = info?.open_hour ?? 9;
    const timeFor = (primary) => (primary && slotTime ? slotTime : `${String(openHour).padStart(2, "0")}:00`);
    const toIso = (d, primary) => new Date(`${d}T${timeFor(primary)}:00`).toISOString();
    setSending(true);
    try {
      await api.post("/public/appointment-requests", {
        ...form,
        ...sim,
        address: sim.address || form.address,
        service: sim.service || form.service,
        slug,
        desired_date: toIso(dates[0], true),
        second_date: dates[1] ? toIso(dates[1], false) : null,
      });
      setDone(true);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setSending(false); }
  };

  if (notFound) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="public-not-found">
        <div className="bg-white border border-slate-200 rounded-2xl p-8 max-w-md text-center">
          <h1 className="font-heading text-xl font-bold text-slate-900 mb-2">Professionnel introuvable</h1>
          <p className="text-sm text-slate-500">Ce lien de réservation n'est pas valide ou n'est plus actif. Demandez à votre professionnel son lien personnel de prise de rendez-vous.</p>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white border border-slate-200 rounded-2xl p-8 max-w-md text-center">
          <div className="w-16 h-16 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-8 h-8 text-emerald-600" />
          </div>
          <h1 className="font-heading text-xl font-bold text-slate-900 mb-2">Demande envoyée !</h1>
          <p className="text-sm text-slate-500">Merci. Votre demande a bien été reçue. {info?.commercial_name || "Notre équipe"} vous recontactera pour confirmer ou proposer une date. Aucune réservation n'est définitive tant qu'elle n'est pas confirmée.</p>
          <Button onClick={() => { setForm(EMPTY); setDates([]); setDone(false); }} className="mt-6 bg-emerald-500 hover:bg-emerald-600 text-black font-semibold" data-testid="btn-new-request">Nouvelle demande</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-700 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="flex flex-col items-center text-center mb-8">
          <div className="w-14 h-14 rounded-2xl bg-emerald-500 flex items-center justify-center mb-3">
            <ShieldCheck className="w-8 h-8 text-black" />
          </div>
          <h1 className="font-heading text-2xl font-extrabold text-slate-900">{info?.commercial_name || "Auto Detail Manager"}</h1>
          <p className="text-sm text-slate-500 mt-1">{info?.public_subtitle || (info?.business_type ? `Demande de rendez-vous — ${info.business_type}` : "Demande de rendez-vous")}</p>
          {(info?.phone || info?.email) && (
            <div className="flex gap-4 mt-2 text-xs text-slate-500">
              {info?.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{info.phone}</span>}
              {info?.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{info.email}</span>}
            </div>
          )}
          {info?.social && Object.values(info.social).some(Boolean) && (
            <div className="flex gap-3 mt-3" data-testid="public-social">
              {Object.entries(info.social).filter(([, v]) => v).map(([k, v]) => (
                <a key={k} href={v} target="_blank" rel="noreferrer" className="text-xs text-emerald-600 hover:underline capitalize">{k}</a>
              ))}
            </div>
          )}
          {info?.payment_methods?.length > 0 && (
            <div className="mt-2 text-[11px] text-slate-400" data-testid="public-payments">Paiements acceptés : {info.payment_methods.join(" · ")}</div>
          )}
        </div>

        {/* Gallery */}
        {showcase.media?.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-2xl p-5 mb-6" data-testid="public-gallery">
            <h2 className="font-heading font-semibold text-slate-900 mb-3">Nos réalisations</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {showcase.media.map((m) => (
                <div key={m.id} className="rounded-lg overflow-hidden border border-slate-200 aspect-square bg-slate-100">
                  {m.type === "video" ? (
                    <video src={fileSrc(m.url || m.path)} controls className="w-full h-full object-cover" />
                  ) : (
                    <img src={fileSrc(m.url || m.path)} alt={m.caption || ""} className="w-full h-full object-cover" />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <PublicSimulator
          services={info?.services || []}
          categories={info?.vehicle_categories || []}
          cfg={{ show_prices: info?.public_show_prices, allow_multi: info?.public_allow_multi, enable_estimate: info?.public_enable_estimate }}
          travelConfig={{ mode: info?.travel_fee_mode, flat: info?.travel_flat_amount, perKm: info?.travel_per_km, roundTrip: info?.travel_round_trip, freeKm: info?.travel_free_km, minAmount: info?.travel_min_amount, tiers: info?.travel_tiers, recupMultiplier: info?.recuperation_multiplier }}
          slug={slug}
          onChange={setSim}
        />

        <form onSubmit={submit} className="bg-white border border-slate-200 rounded-2xl p-6 space-y-5" data-testid="public-request-form">
          <Section title="Vos coordonnées">
            <Grid>
              <F label="Prénom *"><Input data-testid="pr-first-name" value={form.first_name} onChange={set("first_name")} className="bg-slate-50 border-slate-200" /></F>
              <F label="Nom *"><Input data-testid="pr-last-name" value={form.last_name} onChange={set("last_name")} className="bg-slate-50 border-slate-200" /></F>
              <F label="Téléphone *"><Input data-testid="pr-phone" value={form.phone} onChange={set("phone")} className="bg-slate-50 border-slate-200" /></F>
              {bf.email && <F label="Email"><Input type="email" data-testid="pr-email" value={form.email} onChange={set("email")} className="bg-slate-50 border-slate-200" /></F>}
              {bf.company && <F label="Entreprise"><Input data-testid="pr-company" value={form.company} onChange={set("company")} className="bg-slate-50 border-slate-200" /></F>}
              {bf.address && <F label="Adresse d'intervention"><Input data-testid="pr-address" value={form.address} onChange={set("address")} className="bg-slate-50 border-slate-200" /></F>}
            </Grid>
          </Section>

          {bf.vehicle && (
          <Section title="Votre véhicule">
            <Grid>
              <F label="Marque *"><Input data-testid="pr-make" value={form.make} onChange={set("make")} className="bg-slate-50 border-slate-200" /></F>
              <F label="Modèle"><Input value={form.model} onChange={set("model")} className="bg-slate-50 border-slate-200" /></F>
              <F label="Année"><Input value={form.year} onChange={set("year")} className="bg-slate-50 border-slate-200" /></F>
              <F label="Immatriculation (facultatif)"><Input value={form.plate} onChange={set("plate")} className="bg-slate-50 border-slate-200" /></F>
            </Grid>
          </Section>
          )}

          <Section title="Votre demande">
            {bf.service && (
            <F label="Prestation souhaitée">
              <Select value={form.service} onValueChange={(v) => setForm({ ...form, service: v })}>
                <SelectTrigger data-testid="pr-service" className="bg-slate-50 border-slate-200"><SelectValue placeholder="Choisir une prestation" /></SelectTrigger>
                <SelectContent className="bg-white border-slate-200 text-slate-700">
                  {(info?.services || []).map((s) => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}
                  <SelectItem value="Autre / à définir">Autre / à définir</SelectItem>
                </SelectContent>
              </Select>
            </F>
            )}
            {bf.vehicle && <F label="État actuel du véhicule"><Textarea data-testid="pr-condition" value={form.condition} onChange={set("condition")} className="bg-slate-50 border-slate-200" placeholder="Décrivez l'état (taches, rayures, entretien...)" /></F>}

            <div className="space-y-2">
              <Label className="text-xs text-slate-500">Photos (facultatif)</Label>
              <div className="flex flex-wrap gap-2">
                {form.photos.map((p, i) => (
                  <div key={i} className="relative w-20 h-20 rounded-lg overflow-hidden border border-slate-200 group">
                    <img src={fileSrc(p.url || p.path)} alt="" className="w-full h-full object-cover" />
                    <button type="button" onClick={() => setForm({ ...form, photos: form.photos.filter((_, x) => x !== i) })} className="absolute top-0.5 right-0.5 bg-black/70 rounded-full p-0.5 text-red-600"><X className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
                <button type="button" onClick={() => inputRef.current?.click()} data-testid="pr-add-photo" className="w-20 h-20 rounded-lg border border-dashed border-slate-300 flex flex-col items-center justify-center gap-1 text-slate-500 hover:border-emerald-500/50 hover:text-emerald-600">
                  {busyPhoto ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
                  <span className="text-[10px]">Ajouter</span>
                </button>
              </div>
              <input ref={inputRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => e.target.files && uploadPhotos(e.target.files)} />
            </div>

            <div className="space-y-2">
              <Label className="text-xs text-slate-500">Choisissez une date disponible {dates.length > 0 && <span className="text-emerald-600">· {dates.length} sélectionnée(s)</span>}</Label>
              <p className="text-[11px] text-slate-500">Sélectionnez une 1ʳᵉ date (verte). Vous pouvez aussi choisir une 2ᵉ date facultative. Les dates rouges sont complètes, les grises fermées.</p>
              <AvailabilityCalendar selected={dates} onToggle={toggleDate} slug={slug} />
              {dates.length > 0 && (
                <div className="text-xs text-slate-700" data-testid="pr-selected-dates">
                  Date souhaitée : <span className="text-emerald-600 font-medium">{dates[0]}</span>
                  {dates[1] && <> · 2ᵉ choix : <span className="text-emerald-600 font-medium">{dates[1]}</span></>}
                </div>
              )}
            </div>

            {/* Time slots for the primary date */}
            {dates.length > 0 && (
              <div className="space-y-2" data-testid="pr-slots">
                <Label className="text-xs text-slate-500 flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> Créneau horaire pour le {dates[0]}</Label>
                {slotLoading ? (
                  <div className="text-xs text-slate-400 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Chargement des créneaux…</div>
                ) : slotData?.status === "closed" ? (
                  <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">Cette date n'est pas ouverte à la réservation{slotData?.holiday ? ` (${slotData.holiday})` : ""}.</div>
                ) : hasSlots ? (
                  <div className="flex flex-wrap gap-2">
                    {slotData.slots.map((sl) => (
                      <button
                        key={sl.time}
                        type="button"
                        disabled={!sl.available}
                        data-testid={`pr-slot-${sl.time}`}
                        onClick={() => setSlotTime(sl.time)}
                        className={`px-3 py-1.5 rounded-lg text-sm border transition ${!sl.available ? "bg-slate-50 border-slate-200 text-slate-300 line-through cursor-not-allowed" : slotTime === sl.time ? "bg-emerald-500 border-emerald-500 text-black font-semibold" : "bg-white border-slate-200 text-slate-700 hover:border-emerald-400"}`}
                      >{sl.time}</button>
                    ))}
                    {availableSlots.length === 0 && <span className="text-xs text-red-500">Aucun créneau libre ce jour-là.</span>}
                  </div>
                ) : (
                  <div className="text-[11px] text-slate-400">Aucun créneau horaire spécifique — le professionnel vous proposera une heure.</div>
                )}
              </div>
            )}

            <F label="Commentaire"><Textarea value={form.comment} onChange={set("comment")} className="bg-slate-50 border-slate-200" /></F>
          </Section>

          <p className="text-xs text-slate-500">En envoyant ce formulaire, vous proposez une date. La réservation devient effective uniquement après confirmation de {info?.commercial_name || "notre équipe"}.</p>

          <Button type="submit" disabled={sending} data-testid="btn-submit-request" className="w-full h-11 bg-emerald-500 hover:bg-emerald-600 text-black font-semibold">
            {sending && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Envoyer ma demande
          </Button>
        </form>

        {/* Reviews */}
        <div className="bg-white border border-slate-200 rounded-2xl p-5 mt-6" data-testid="public-reviews">
          <h2 className="font-heading font-semibold text-slate-900 mb-3">Avis clients</h2>
          {showcase.reviews?.length > 0 ? (
            <div className="space-y-3 mb-4">
              {showcase.reviews.map((r) => (
                <div key={r.id} className="border border-slate-100 rounded-xl p-3">
                  <div className="flex items-center gap-2">
                    <Stars n={r.rating} />
                    <span className="text-sm font-medium text-slate-800">{r.author}</span>
                  </div>
                  {r.text && <p className="text-sm text-slate-600 mt-1">{r.text}</p>}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-slate-400 mb-4">Soyez le premier à laisser un avis.</p>
          )}
          <ReviewForm slug={slug} />
        </div>

        {/* FAQ */}
        {showcase.faqs?.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-2xl p-5 mt-6" data-testid="public-faq">
            <h2 className="font-heading font-semibold text-slate-900 mb-3">Questions fréquentes</h2>
            <div className="space-y-3">
              {showcase.faqs.map((f) => (
                <div key={f.id} className="border-b border-slate-100 pb-3 last:border-0">
                  <p className="text-sm font-medium text-slate-800">{f.question}</p>
                  <p className="text-sm text-slate-600 mt-1 whitespace-pre-wrap">{f.answer}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Stars({ n = 0 }) {
  return (
    <span className="flex">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} className={`w-4 h-4 ${i <= n ? "text-amber-400 fill-amber-400" : "text-slate-300"}`} />
      ))}
    </span>
  );
}

function ReviewForm({ slug }) {
  const [author, setAuthor] = useState("");
  const [rating, setRating] = useState(5);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async () => {
    if (!author.trim()) return toast.error("Merci d'indiquer votre nom");
    setSending(true);
    try {
      await api.post("/public/reviews", { slug, author, rating, text });
      setSent(true); setAuthor(""); setText(""); setRating(5);
      toast.success("Merci ! Votre avis sera publié après validation.");
    } catch (e) { toast.error("Envoi impossible"); } finally { setSending(false); }
  };

  if (sent) return <p className="text-sm text-emerald-600" data-testid="review-thanks">Merci pour votre avis ! Il sera visible après validation du professionnel.</p>;

  return (
    <div className="border-t border-slate-100 pt-4 space-y-3" data-testid="public-review-form">
      <p className="text-xs font-medium text-slate-700">Laisser un avis</p>
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((i) => (
          <button key={i} type="button" data-testid={`review-star-${i}`} onClick={() => setRating(i)}>
            <Star className={`w-6 h-6 ${i <= rating ? "text-amber-400 fill-amber-400" : "text-slate-300"}`} />
          </button>
        ))}
      </div>
      <Input data-testid="review-author" placeholder="Votre nom" value={author} onChange={(e) => setAuthor(e.target.value)} className="bg-slate-50 border-slate-200" />
      <Textarea data-testid="review-text" placeholder="Votre expérience (facultatif)" value={text} onChange={(e) => setText(e.target.value)} className="bg-slate-50 border-slate-200" />
      <Button type="button" onClick={submit} disabled={sending} data-testid="review-submit" className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold">
        {sending && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Envoyer mon avis
      </Button>
    </div>
  );
}

function Section({ title, children }) {
  return <div className="space-y-3"><h3 className="text-xs uppercase tracking-widest text-emerald-600/80 font-semibold">{title}</h3>{children}</div>;
}
function Grid({ children }) { return <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>; }
function F({ label, children }) { return <div className="space-y-1.5"><Label className="text-xs text-slate-500">{label}</Label>{children}</div>; }

// Mini-simulateur public — MÊME moteur tarifaire que la gestion. Le client choisit
// ses prestations, son gabarit, son lieu ; l'estimation et la demande sont pré-remplies.
function priceLabel(s, cat) {
  if (s.public_price_visible === false) return null;
  const t = s.pricing_type || (s.price == null ? "sur_devis" : "fixe");
  if (t === "sur_devis" || s.price == null) return "Sur devis";
  const supp = !s.non_vehicle && cat ? Number((s.vehicle_supplements || {})[cat] || 0) : 0;
  return (t === "a_partir_de" ? "À partir de " : "") + eur(Number(s.price) + supp);
}

function PublicSimulator({ services = [], categories = [], cfg = {}, travelConfig = {}, slug, onChange }) {
  const visible = services.filter((s) => s.show_public !== false);
  const bookable = visible.filter((s) => s.public_bookable !== false);
  const [sel, setSel] = useState([]);
  const [cat, setCat] = useState(categories[0]?.id || null);
  const [locType, setLocType] = useState("mon_local");
  const [addr, setAddr] = useState("");
  const [travel, setTravel] = useState({ mode: travelConfig.mode || "aucun", distance_km: 0, amount: 0 });
  const [computing, setComputing] = useState(false);
  const [geoMsg, setGeoMsg] = useState(null);
  if (bookable.length === 0) return null;
  const showPrices = cfg.show_prices !== false;
  const allowMulti = cfg.allow_multi !== false;
  const enableEstimate = cfg.enable_estimate !== false && showPrices;
  const travelMode = travelConfig.mode || "aucun";
  const cats = Array.from(new Set(bookable.map((s) => s.category || "AUTRES")));
  const lines = sel.map((id) => { const s = bookable.find((x) => x.id === id); return s ? lineFromService(s, cat) : null; }).filter(Boolean);
  const hasVehicle = lines.some((li) => !li.non_vehicle);
  const totals = computePricing({ line_items: lines, travel: (enableEstimate && locType !== "mon_local") ? travel : null });

  const emit = (over = {}) => {
    const st = { sel, cat, locType, addr, travel, ...over };
    const ls = st.sel.map((id) => bookable.find((x) => x.id === id)).filter(Boolean);
    const t = computePricing({ line_items: ls.map((x) => lineFromService(x, st.cat)), travel: (enableEstimate && st.locType !== "mon_local") ? st.travel : null });
    onChange && onChange({
      service_ids: st.sel,
      service: ls.map((x) => x.name).join(", ") || null,
      vehicle_category: hasVehicleFor(ls) ? st.cat : null,
      location_type: st.locType,
      address: st.locType === "mon_local" ? "" : st.addr,
      travel: st.locType === "mon_local" ? null : st.travel,
      estimate_total: enableEstimate ? t.total : null,
    });
  };
  const hasVehicleFor = (ls) => ls.some((x) => !x.non_vehicle);
  const toggle = (id) => { const next = sel.includes(id) ? sel.filter((x) => x !== id) : (allowMulti ? [...sel, id] : [id]); setSel(next); emit({ sel: next }); };
  const chooseCat = (id) => { setCat(id); emit({ cat: id }); };
  const chooseLoc = (k) => { const nt = k === "mon_local" ? { mode: travelMode, distance_km: 0, amount: 0 } : travel; setLocType(k); setTravel(nt); emit({ locType: k, travel: nt }); };
  const setAddress = (val) => { setAddr(val); emit({ addr: val }); };
  const autoDist = async () => {
    if (!addr) { setGeoMsg("Renseignez votre adresse."); return; }
    setComputing(true); setGeoMsg(null);
    try {
      const { data } = await api.post("/public/geo/distance", { slug, destination: addr });
      if (data && data.status === "ok") {
        const amt = suggestTravel({ mode: travelMode, locationType: locType, distanceKm: data.distance_km, flat: travelConfig.flat, perKm: travelConfig.perKm, roundTrip: travelConfig.roundTrip, freeKm: travelConfig.freeKm, minAmount: travelConfig.minAmount, tiers: travelConfig.tiers, recupMultiplier: travelConfig.recupMultiplier });
        const nt = { mode: travelMode, distance_km: data.distance_km, amount: amt };
        setTravel(nt); emit({ travel: nt });
      } else setGeoMsg((data && data.message) || "Distance indisponible — le professionnel confirmera.");
    } catch { setGeoMsg("Distance indisponible — le professionnel confirmera."); } finally { setComputing(false); }
  };
  const showTravel = locType !== "mon_local" && travelMode !== "aucun";

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 mb-6" data-testid="public-simulator">
      <h2 className="font-heading font-semibold text-slate-900 mb-1">Estimez votre prestation</h2>
      <p className="text-xs text-slate-500 mb-4">{allowMulti ? "Sélectionnez une ou plusieurs prestations." : "Sélectionnez votre prestation."} L'estimation est indicative ; le professionnel confirme le devis.</p>
      <div className="space-y-4">
        {cats.map((ct) => (
          <div key={ct}>
            <h3 className="text-xs uppercase tracking-widest text-emerald-600/80 font-semibold mb-2">{ct}</h3>
            <div className="space-y-1.5">
              {bookable.filter((s) => (s.category || "AUTRES") === ct).map((s) => {
                const on = sel.includes(s.id);
                const lbl = showPrices ? priceLabel(s, cat) : null;
                return (
                  <button key={s.id} type="button" onClick={() => toggle(s.id)} data-testid={`sim-service-${s.id}`}
                    className={`w-full flex items-center justify-between gap-3 text-sm rounded-lg px-3 py-2 border transition text-left ${on ? "bg-emerald-50 border-emerald-300" : "bg-slate-50 border-slate-200"}`}>
                    <span className="text-slate-700">{on ? "✓ " : ""}{s.name}{s.non_vehicle && <span className="ml-1.5 text-[10px] uppercase text-slate-400">hors véhicule</span>}</span>
                    {lbl && <span className="font-mono text-emerald-600 shrink-0">{lbl}</span>}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {hasVehicle && categories.length > 0 && (
        <div className="mt-4" data-testid="sim-categories">
          <Label className="text-xs text-slate-500">Gabarit du véhicule</Label>
          <div className="flex flex-wrap gap-2 mt-2">
            {categories.map((c) => (
              <button key={c.id} type="button" onClick={() => chooseCat(c.id)} data-testid={`sim-cat-${c.id}`}
                className={`px-3 py-1.5 rounded-lg border text-sm transition ${cat === c.id ? "bg-emerald-500 border-emerald-500 text-black font-semibold" : "bg-slate-50 border-slate-200 text-slate-600"}`}>{c.name}</button>
            ))}
          </div>
        </div>
      )}

      {travelMode !== "aucun" && (
        <div className="mt-4" data-testid="sim-location">
          <Label className="text-xs text-slate-500">Lieu d'intervention</Label>
          <div className="grid grid-cols-3 gap-2 mt-2">
            {[["mon_local", "Au local"], ["chez_client", "Chez moi"], ["recuperation_livraison", "Récup + livr."]].map(([k, l]) => (
              <button key={k} type="button" onClick={() => chooseLoc(k)} data-testid={`sim-loc-${k}`}
                className={`py-2 px-1 rounded-lg border text-xs transition ${locType === k ? "bg-emerald-500 text-black border-emerald-500 font-semibold" : "bg-slate-50 border-slate-200 text-slate-500"}`}>{l}</button>
            ))}
          </div>
          {showTravel && (
            <div className="mt-2 space-y-2">
              <Input value={addr} onChange={(e) => setAddress(e.target.value)} placeholder="Votre adresse complète" data-testid="sim-address" className="bg-slate-50 border-slate-200" />
              <div className="flex items-center gap-2">
                <Button type="button" variant="outline" size="sm" onClick={autoDist} disabled={computing} data-testid="sim-auto-distance" className="h-8 border-slate-200">{computing ? "Calcul…" : "Estimer le déplacement"}</Button>
                {travel.amount > 0 && <span className="text-xs text-slate-600">Déplacement : <span className="font-mono">{eur(travel.amount)}</span>{travel.distance_km ? ` (${travel.distance_km} km)` : ""}</span>}
              </div>
              {geoMsg && <p className="text-[11px] text-amber-600" data-testid="sim-geo-msg">{geoMsg}</p>}
            </div>
          )}
        </div>
      )}

      {enableEstimate && sel.length > 0 && (
        <div className="mt-4 rounded-xl border-2 border-emerald-500/40 bg-emerald-500/[0.04] p-3 space-y-1" data-testid="sim-estimate">
          {lines.map((li, i) => {
            const unit = (Number(li.unit_price) || 0) + (li.non_vehicle ? 0 : (Number(li.vehicle_supplement) || 0));
            const isDevis = li.unit_price == null;
            return (
              <div key={i} className="flex items-center justify-between text-sm" data-testid={`sim-line-${i}`}>
                <span className="text-slate-600">{li.name}{li.pricing_type === "a_partir_de" ? " (à partir de)" : ""}</span>
                <span className="font-mono text-slate-700">{isDevis ? "Sur devis" : eur(unit)}</span>
              </div>
            );
          })}
          {totals.travelAmount > 0 && <div className="flex items-center justify-between text-sm"><span className="text-slate-600">Déplacement</span><span className="font-mono text-slate-700">{eur(totals.travelAmount)}</span></div>}
          <div className="flex items-center justify-between border-t border-emerald-500/20 pt-1.5 mt-1">
            <span className="font-heading font-bold text-slate-900">TOTAL ESTIMÉ</span>
            <span className="font-mono font-bold text-emerald-700 text-lg" data-testid="sim-total">{eur(totals.total)}</span>
          </div>
          {totals.hasPending && <p className="text-[11px] text-amber-600">Certaines prestations sont sur devis — tarif confirmé par le professionnel.</p>}
          <p className="text-[10px] text-slate-400">Estimation indicative. Choisissez votre créneau ci-dessous puis « Envoyer ma demande ».</p>
        </div>
      )}
      {!showPrices && <p className="text-[11px] text-slate-400 mt-3">Devis personnalisé — le professionnel vous communiquera le tarif.</p>}
    </div>
  );
}
